import { AxiosInstance } from "axios";
import { ApiResponse, User } from "./auth";
import { PaginatedCases } from "./cases";

export interface CreateUserResponse {
  userId: string;
  name: string;
  email: string;
  role: string;
  status: string;
  specialization?: string;
}

export interface CreateUserData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  specialty?: string;
  institution?: string;
  professionalRegistration?: string;
  yearsOfExperience?: number;
  phone?: string;
}

export class AdminApi {
  constructor(private api: AxiosInstance) {}

  // Create admin user
  async createAdmin(userData: CreateUserData): Promise<ApiResponse<CreateUserResponse>> {
    try {
      const response = await this.api.post('/admin/create-admin', userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to create admin');
    }
  }

  // Create specialist user
  async createSpecialist(userData: CreateUserData): Promise<ApiResponse<CreateUserResponse>> {
    try {
      const response = await this.api.post('/admin/create-specialist', userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to create specialist');
    }
  }

  // Create physician user
  async createPhysician(userData: CreateUserData): Promise<ApiResponse<CreateUserResponse>> {
    try {
      const response = await this.api.post('/admin/create-physician', userData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to create physician');
    }
  }

  // Get all users (admin function)
  async getAllUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    status?: string;
  }): Promise<ApiResponse<{
    users: User[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalUsers: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  }>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.role) queryParams.append('role', params.role);
      if (params?.status) queryParams.append('status', params.status);
      
      const response = await this.api.get(`/admin/users?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get users');
    }
  }

  // Get all cases (admin function)
  async getAllCases(params?: {
    page?: number;
    limit?: number;
    status?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<PaginatedCases>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.status) queryParams.append('status', params.status);
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      const response = await this.api.get(`/admin/cases?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to get all cases');
    }
  }

  // Update user role/status
  async updateUser(userId: string, updateData: {
    role?: string;
    status?: string;
    profile?: any;
  }): Promise<ApiResponse<User>> {
    try {
      const response = await this.api.put(`/admin/users/${userId}`, updateData);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to update user');
    }
  }

  // Delete user
  async deleteUser(userId: string): Promise<ApiResponse<{}>> {
    try {
      const response = await this.api.delete(`/admin/users/${userId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Failed to delete user');
    }
  }
}
